// Vendor Program v2 PR 2 — the Vendor portal "Monthly reports" tab.
// Per-assignment groups: due/overdue chips for outstanding periods (click =
// idempotent draft POST → form), report history, and the four-section
// monthly report form with debounced autosave (full payload, FIND-12),
// a returned-state correction callout, the formal-complaint modal, and a
// summarising submit confirmation. Built so a non-technical vendor finishes
// the month in minutes.

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import {
  AutosaveIndicator,
  Button,
  EmptyState,
  Input,
  Label,
  SkeletonList,
  StatusBadge,
  StatusStepper,
  SubmitButton,
  SubmittingHint,
  Textarea,
  Timestamp,
  useSubmittingMutation,
  useToast,
  type AutosaveState,
  type StatusTone,
} from "@/design-system/components/ui";
import {
  portalVendorApi,
  type PortalVendorAssignmentReportsDto,
  type PortalVendorReportListItemDto,
  type UpdateVendorReportRequest,
  type VendorMonthlyReportDto,
  type VendorReportStatus,
} from "../api/vendor";

const AUTOSAVE_DELAY_MS = 900;
/** A period's report is "overdue" once its periodEnd is >15 days past. */
const OVERDUE_AFTER_MS = 15 * 24 * 60 * 60 * 1000;

const STATUS_LABEL: Record<VendorReportStatus, string> = {
  draft: "Draft",
  submitted: "Submitted",
  returned: "Returned",
  accepted: "Accepted",
  "signed-off": "Signed off",
  void: "Void",
};

function statusTone(status: VendorReportStatus): StatusTone {
  switch (status) {
    case "submitted":
      return "info";
    case "returned":
      return "warn";
    case "accepted":
    case "signed-off":
      return "ok";
    case "void":
      return "muted";
    default:
      return "neutral";
  }
}

/** "June 2026" for an ISO month-start (periods are UTC month boundaries). */
function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** End of the month an ISO month-start opens (= the next month's start). */
function periodEndOf(monthStartIso: string): number {
  const d = new Date(monthStartIso);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
}

function isPeriodOverdue(monthStartIso: string, now = Date.now()): boolean {
  return now - periodEndOf(monthStartIso) > OVERDUE_AFTER_MS;
}

export function VendorReportsTab() {
  const [groups, setGroups] = useState<PortalVendorAssignmentReportsDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const openReportId = params.get("report");

  const load = useCallback(async () => {
    try {
      setGroups(await portalVendorApi.listReports());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load your reports.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const setOpenReportId = (id: string | null) => {
    const updated = new URLSearchParams(params);
    if (id) updated.set("report", id);
    else updated.delete("report");
    setParams(updated, { replace: true });
  };

  const startDraft = useSubmittingMutation(
    async (assignmentId: string, periodStart: string) => {
      const draft = await portalVendorApi.createReportDraft(assignmentId, periodStart);
      setOpenReportId(draft.id);
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Couldn't start the report."),
    },
  );

  if (openReportId) {
    return (
      <ReportFormSurface
        reportId={openReportId}
        onBack={() => {
          setOpenReportId(null);
          void load();
        }}
      />
    );
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!groups) {
    return <SkeletonList data-testid="reports-loading" />;
  }
  if (groups.length === 0) {
    return (
      <EmptyState
        icon={ClipboardList}
        title="No reports yet"
        description="When Phoenix Capital assigns you to a client, your monthly reports will appear here."
      />
    );
  }

  const startPeriod = (assignmentId: string, periodStart: string) => {
    void startDraft.submit(assignmentId, periodStart);
  };

  return (
    <section aria-label="Monthly reports" className="flex flex-col gap-space-4">
      {groups.map((group) => (
        <div
          key={group.assignmentId}
          className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3"
          data-testid={`assignment-group-${group.assignmentId}`}
        >
          <div className="flex items-baseline justify-between gap-space-2 flex-wrap">
            <h2 className="font-display font-semibold text-h4 m-0">{group.loanLabel}</h2>
            <span className="text-caption text-gray-mid">{group.roleOnLoan}</span>
          </div>

          {group.outstandingPeriods.length > 0 && (
            <div className="flex flex-wrap gap-space-2" data-testid="outstanding-periods">
              {group.outstandingPeriods.map((period) => {
                const overdue = isPeriodOverdue(period);
                return (
                  <button
                    key={period}
                    type="button"
                    className={
                      "inline-flex items-center gap-space-1 rounded-pill border px-space-3 py-space-1 text-caption font-medium " +
                      "transition-colors duration-fast ease-standard hover:underline " +
                      (overdue
                        ? "border-status-error bg-status-error/10 text-status-error"
                        : "border-status-warning bg-status-warning/15 text-status-warning")
                    }
                    disabled={startDraft.pending}
                    onClick={() => startPeriod(group.assignmentId, period)}
                    data-testid={`start-period-${group.assignmentId}-${period}`}
                  >
                    {monthLabel(period)} report {overdue ? "overdue" : "due"} — start now
                  </button>
                );
              })}
              <SubmittingHint pending={startDraft.pending} message="Starting your report…" className="w-full" />
            </div>
          )}

          {group.reports.length > 0 ? (
            <ul className="m-0 p-0 list-none flex flex-col" data-testid="report-history">
              {group.reports.map((report) => (
                <li key={report.id} className="border-b border-border-light last:border-b-0">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-space-2 py-space-2 text-left hover:bg-white-warm"
                    onClick={() => setOpenReportId(report.id)}
                    data-testid={`report-row-${report.id}`}
                  >
                    <span className="text-small font-medium text-onyx">
                      {monthLabel(report.periodStart)}
                    </span>
                    <span className="flex items-center gap-space-2">
                      <ResubmittedCaption report={report} />
                      <Timestamp value={report.submittedAt} prefix="Submitted" />
                      <StatusBadge tone={statusTone(report.status)} struck={report.status === "void"}>
                        {STATUS_LABEL[report.status]}
                      </StatusBadge>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            group.outstandingPeriods.length === 0 && (
              <p className="text-caption text-gray-mid m-0">No reports yet for this client.</p>
            )
          )}
        </div>
      ))}
    </section>
  );
}

function ResubmittedCaption({ report }: { report: PortalVendorReportListItemDto }) {
  if (report.status !== "submitted" || report.revisionNumber <= 1) return null;
  return <span className="text-caption text-gray-mid">Resubmitted</span>;
}

// ── the four-section report form ─────────────────────────────────────────

interface CollectedRowState {
  /** Raw text of the currency input ("" = not entered). */
  amount: string;
  serviceDescription: string;
}

interface ReportFormState {
  publicNarrative: string;
  privateConcerns: string;
  /** Raw text of the currency input ("" = not entered). */
  invoiceAmount: string;
  amountCalculationExplanation: string;
  grades: { goalId: string; score: number; isComplete: boolean; note: string }[];
  /** Regulated §4 — fees-for-services rows (billed to the borrower through
   *  Phoenix). */
  collected: CollectedRowState[];
  nothingCollected: boolean;
  /** SP-4 WS-2 — raw text of the quantity input (hours / transaction value;
   *  "" = not entered). Shown only for hourly/percent contract terms. */
  reportedQuantity: string;
}

function toFormState(report: VendorMonthlyReportDto): ReportFormState {
  return {
    publicNarrative: report.publicNarrative ?? "",
    privateConcerns: report.privateConcerns ?? "",
    invoiceAmount:
      report.selfReportedInvoiceAmount != null
        ? String(report.selfReportedInvoiceAmount)
        : "",
    amountCalculationExplanation: report.amountCalculationExplanation ?? "",
    grades: report.goalGrades.map((g) => ({
      goalId: g.goalId,
      score: g.score,
      isComplete: g.isComplete,
      note: g.note ?? "",
    })),
    collected: (report.collectedLineItems ?? []).map((line) => ({
      amount: String(line.amount),
      serviceDescription: line.serviceDescription,
    })),
    nothingCollected: report.collectedNothingConfirmed ?? false,
    reportedQuantity:
      report.reportedQuantity != null ? String(report.reportedQuantity) : "",
  };
}

function parseAmount(raw: string): number | null {
  const cleaned = raw.replace(/[$,\s]/g, "");
  if (cleaned === "") return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** A collected row that satisfies the regulated §4 submit gate. */
function isValidCollectedRow(row: CollectedRowState): boolean {
  const amount = parseAmount(row.amount);
  return amount != null && amount > 0 && row.serviceDescription.trim() !== "";
}

/** The FULL autosave payload — every field, every grade, every time. */
function toPayload(form: ReportFormState): UpdateVendorReportRequest {
  return {
    publicNarrative: form.publicNarrative.trim() === "" ? null : form.publicNarrative,
    privateConcerns: form.privateConcerns.trim() === "" ? null : form.privateConcerns,
    selfReportedInvoiceAmount: parseAmount(form.invoiceAmount),
    amountCalculationExplanation:
      form.amountCalculationExplanation.trim() === ""
        ? null
        : form.amountCalculationExplanation,
    goalGrades: form.grades.map((g) => ({
      goalId: g.goalId,
      score: g.score,
      isComplete: g.isComplete,
      note: g.note.trim() === "" ? null : g.note,
    })),
    // Fully-empty rows (a just-added blank line) are not sent; a partially
    // typed row rides along so nothing is lost between autosaves.
    collectedLineItems: form.collected
      .filter((row) => row.amount.trim() !== "" || row.serviceDescription.trim() !== "")
      .map((row) => ({
        amount: parseAmount(row.amount) ?? 0,
        serviceDescription: row.serviceDescription.trim(),
      })),
    collectedNothingConfirmed: form.nothingCollected,
    reportedQuantity: parseAmount(form.reportedQuantity),
  };
}

const LADDER_STEPS = [
  { key: "draft", label: "Draft", hint: "You're filling this in — nothing is sent yet." },
  { key: "submitted", label: "Submitted", hint: "Phoenix is reviewing your report." },
  { key: "accepted", label: "Accepted", hint: "Phoenix accepted your report." },
  { key: "signed-off", label: "Signed off", hint: "The borrower signed off on your month." },
];

function ladderIndex(status: VendorReportStatus): number {
  switch (status) {
    case "submitted":
      return 1;
    case "accepted":
      return 2;
    case "signed-off":
      return 3;
    default:
      // draft + returned: the report is back in the vendor's hands.
      return 0;
  }
}

function ReportFormSurface({ reportId, onBack }: { reportId: string; onBack: () => void }) {
  const toast = useToast();
  const [report, setReport] = useState<VendorMonthlyReportDto | null>(null);
  const [form, setForm] = useState<ReportFormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<AutosaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [complaining, setComplaining] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef<string>("");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const dto = await portalVendorApi.getReport(reportId);
        if (cancelled) return;
        lastSentRef.current = JSON.stringify(toPayload(toFormState(dto)));
        setReport(dto);
        setForm(toFormState(dto));
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load the report.");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const editable = report?.status === "draft" || report?.status === "returned";

  const flushSave = useCallback(
    async (state: ReportFormState) => {
      const payload = toPayload(state);
      const blob = JSON.stringify(payload);
      if (blob === lastSentRef.current) return;
      setSaveState("saving");
      try {
        await portalVendorApi.updateReport(reportId, payload);
        lastSentRef.current = blob;
        setLastSavedAt(new Date().toISOString());
        setSaveState("saved");
      } catch {
        setSaveState("error");
      }
    },
    [reportId],
  );

  useEffect(() => {
    if (!form || !editable) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void flushSave(form);
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [form, editable, flushSave]);

  const submitMutation = useSubmittingMutation(
    async (state: ReportFormState) => {
      // Flush any pending autosave so the server grades what's on screen.
      if (timer.current) clearTimeout(timer.current);
      await flushSave(state);
      const dto = await portalVendorApi.submitReport(reportId);
      setReport(dto);
      setConfirming(false);
      toast.success("Report submitted to Phoenix Capital.");
    },
    {
      onError: (err) =>
        setSubmitError(err instanceof Error ? err.message : "Couldn't submit the report."),
    },
  );
  const submitting = submitMutation.pending;

  if (error) {
    return (
      <div className="flex flex-col items-start gap-space-3">
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
        <Button variant="secondary" size="sm" onClick={onBack}>
          Back to reports
        </Button>
      </div>
    );
  }
  if (!report || !form) {
    return <SkeletonList data-testid="report-form-loading" />;
  }

  const isRegulated = report.isRegulatedVendor === true;

  const missingNarrative = form.publicNarrative.trim() === "";
  const missingAmount = !isRegulated && parseAmount(form.invoiceAmount) === null;
  // Regulated §4 gate: ≥1 valid collected line OR the explicit
  // "Nothing collected this month" confirmation.
  const missingCollected =
    isRegulated && !form.nothingCollected && !form.collected.some(isValidCollectedRow);
  const submitBlocked = missingNarrative || missingAmount || missingCollected;
  const submitBlockedReason = submitBlocked
    ? [
        missingNarrative ? "§1 (what we accomplished)" : null,
        missingAmount ? "§4 (invoice amount)" : null,
        missingCollected ? "§4 (fees for services)" : null,
      ]
        .filter(Boolean)
        .join(" and ") + " must be filled in before you can submit."
    : null;

  // SP-4 WS-2 — the quantity input renders only when the vendor's contract
  // terms need one to check the math (hourly → hours; percent → the value).
  const quantityLabel =
    report.contractTermBasis === "Hourly"
      ? "Hours worked this period"
      : report.contractTermBasis === "PercentOfValue"
        ? "Total transaction value this period (USD)"
        : null;

  const update = (patch: Partial<ReportFormState>) =>
    setForm((prev) => (prev ? { ...prev, ...patch } : prev));
  const updateGrade = (
    goalId: string,
    patch: Partial<ReportFormState["grades"][number]>,
  ) =>
    setForm((prev) =>
      prev
        ? {
            ...prev,
            grades: prev.grades.map((g) => (g.goalId === goalId ? { ...g, ...patch } : g)),
          }
        : prev,
    );
  const updateCollectedRow = (index: number, patch: Partial<CollectedRowState>) =>
    setForm((prev) =>
      prev
        ? {
            ...prev,
            collected: prev.collected.map((row, i) =>
              i === index ? { ...row, ...patch } : row,
            ),
          }
        : prev,
    );
  const addCollectedRow = () =>
    setForm((prev) =>
      prev
        ? { ...prev, collected: [...prev.collected, { amount: "", serviceDescription: "" }] }
        : prev,
    );
  const removeCollectedRow = (index: number) =>
    setForm((prev) =>
      prev
        ? { ...prev, collected: prev.collected.filter((_, i) => i !== index) }
        : prev,
    );

  const submit = (state: ReportFormState) => {
    setSubmitError(null);
    void submitMutation.submit(state);
  };

  return (
    <section
      aria-label={`Monthly report — ${monthLabel(report.periodStart)}`}
      className="flex flex-col gap-space-4"
      data-testid="report-form"
    >
      <button
        type="button"
        className="self-start text-small text-gold-shadow hover:underline"
        onClick={onBack}
        data-testid="report-form-back"
      >
        ← Back to reports
      </button>

      <div className="flex flex-col gap-space-2">
        <div className="flex items-center justify-between gap-space-2 flex-wrap">
          <h2 className="font-display font-bold text-h2 m-0">
            {monthLabel(report.periodStart)} — {report.loanNumber}
          </h2>
          <span className="text-caption text-gray-mid">{report.roleOnLoan}</span>
        </div>
        {report.status === "void" ? (
          <StatusBadge tone="muted" struck>
            Void
          </StatusBadge>
        ) : (
          <div className="flex items-center gap-space-2 flex-wrap">
            <StatusStepper
              steps={LADDER_STEPS}
              currentIndex={ladderIndex(report.status)}
              complete={report.status === "signed-off"}
              label="Report status"
              data-testid="report-status-ladder"
            />
            {report.status === "returned" && (
              <StatusBadge tone="warn" data-testid="returned-chip">
                Returned
              </StatusBadge>
            )}
          </div>
        )}
      </div>

      {report.status === "returned" && report.latestReviewComment && (
        <div
          className="rounded-md border border-status-warning bg-status-warning/15 p-space-3"
          role="alert"
          data-testid="returned-callout"
        >
          <p className="text-small font-semibold text-onyx m-0">
            Phoenix asked for a correction:
          </p>
          <p className="text-small text-onyx m-0 mt-space-1 whitespace-pre-wrap">
            {report.latestReviewComment}
          </p>
        </div>
      )}

      {!editable && report.status !== "void" && (
        <p className="text-caption text-gray-mid m-0" data-testid="read-only-note">
          This report has been submitted and can no longer be edited.
        </p>
      )}

      {/* §1 — public narrative */}
      <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-2">
        <h3 className="font-display font-semibold text-h4 m-0">1. What we accomplished</h3>
        <p className="text-caption text-gray-mid m-0">
          This will be viewable by the borrower.
        </p>
        <Textarea
          rows={5}
          value={form.publicNarrative}
          onChange={(e) => update({ publicNarrative: e.target.value })}
          disabled={!editable}
          placeholder="Summarize the work you delivered for this client this month."
          aria-label="What we accomplished"
          data-testid="narrative-input"
        />
      </div>

      {/* §2 — goal grades */}
      <div className="rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3">
        <h3 className="font-display font-semibold text-h4 m-0">2. Goal progress</h3>
        {report.goalGrades.length === 0 ? (
          <p className="text-caption text-gray-mid m-0">No goals are set for this engagement.</p>
        ) : (
          report.goalGrades.map((goal) => {
            const grade = form.grades.find((g) => g.goalId === goal.goalId);
            if (!grade) return null;
            return (
              <div
                key={goal.goalId}
                className="rounded-md border border-border-light p-space-3 flex flex-col gap-space-2"
                data-testid={`goal-card-${goal.goalId}`}
              >
                <p className="text-small font-medium text-onyx m-0">{goal.goalTitle}</p>
                {goal.completionCriteria && (
                  <p className="text-caption text-gray-mid m-0">
                    Done when: {goal.completionCriteria}
                  </p>
                )}
                {/* "months" is the unit every other surface prints (the goal
                    drawer, the goals tab, the borrower card, the vendor's
                    customers tab). Without it, a month-window like "1-6"
                    reads as a quantity beside the progress score. */}
                {goal.targetMonths && (
                  <p className="text-caption text-gray-mid m-0">
                    Target: months {goal.targetMonths}
                  </p>
                )}
                <div className="flex items-center gap-space-3 flex-wrap">
                  <label className="flex items-center gap-space-2 grow text-small">
                    <span className="sr-only">Progress for {goal.goalTitle}</span>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={grade.score}
                      onChange={(e) =>
                        updateGrade(goal.goalId, { score: Number(e.target.value) })
                      }
                      disabled={!editable}
                      className="grow accent-gold"
                      data-testid={`goal-score-${goal.goalId}`}
                    />
                    <span className="text-small tabular-nums w-16 text-right">
                      {grade.score} / 100
                    </span>
                  </label>
                  <label className="flex items-center gap-space-1 text-small">
                    <input
                      type="checkbox"
                      checked={grade.isComplete}
                      onChange={(e) =>
                        updateGrade(goal.goalId, { isComplete: e.target.checked })
                      }
                      disabled={!editable}
                      className="accent-gold"
                      data-testid={`goal-complete-${goal.goalId}`}
                    />
                    Mark complete
                  </label>
                </div>
                <div className="flex flex-col gap-space-1">
                  <Input
                    value={grade.note}
                    onChange={(e) => updateGrade(goal.goalId, { note: e.target.value })}
                    disabled={!editable}
                    placeholder="Optional note on this goal"
                    aria-label={`Note for ${goal.goalTitle}`}
                    data-testid={`goal-note-${goal.goalId}`}
                  />
                  <p className="text-caption text-gray-mid m-0">
                    Visible to the borrower and Phoenix.
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* §3 — private to Phoenix */}
      <div
        className="rounded-lg border border-onyx/30 bg-white-warm p-space-4 flex flex-col gap-space-2"
        data-testid="private-section"
      >
        <h3 className="font-display font-semibold text-h4 m-0">3. Concerns</h3>
        <p className="text-caption font-medium text-onyx m-0">
          Private to Phoenix — the borrower never sees this.
        </p>
        <Textarea
          rows={3}
          value={form.privateConcerns}
          onChange={(e) => update({ privateConcerns: e.target.value })}
          disabled={!editable}
          placeholder="Anything Phoenix should know about this engagement."
          aria-label="Private concerns"
          data-testid="concerns-input"
        />
        <div className="flex items-center gap-space-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setComplaining(true)}
            data-testid="open-complaint"
          >
            Make a formal complaint
          </Button>
          <span className="text-caption text-gray-mid">
            Sent to Phoenix immediately — it doesn't wait for this report.
          </span>
        </div>
      </div>

      {/* §4 — invoice amount (regulated vendors report their fees for
          services instead; SP-4: those fees are billed to the borrower
          through Phoenix, so the vendor never invoices anyone directly) */}
      {isRegulated ? (
        <div
          className="rounded-lg border border-onyx/30 bg-white-warm p-space-4 flex flex-col gap-space-2"
          data-testid="collected-section"
        >
          <h3 className="font-display font-semibold text-h4 m-0">
            4. Fees for services this month
          </h3>
          <p className="text-caption font-medium text-onyx m-0">
            Private to Phoenix — not shared directly. These fees are billed to
            the borrower through Phoenix; you don't invoice the borrower
            yourself.
          </p>
          {form.collected.map((row, index) => (
            <div
              key={index}
              className="flex items-end gap-space-2 flex-wrap"
              data-testid={`collected-row-${index}`}
            >
              <div className="flex flex-col gap-space-1 w-36">
                <Label htmlFor={`collected-amount-${index}`}>Amount (USD)</Label>
                <Input
                  id={`collected-amount-${index}`}
                  inputMode="decimal"
                  value={row.amount}
                  onChange={(e) => updateCollectedRow(index, { amount: e.target.value })}
                  disabled={!editable}
                  placeholder="0.00"
                  className="text-right tabular-nums"
                  data-testid={`collected-amount-${index}`}
                />
              </div>
              <div className="flex flex-col gap-space-1 grow min-w-[12rem]">
                <Label htmlFor={`collected-description-${index}`}>What it was for</Label>
                <Input
                  id={`collected-description-${index}`}
                  value={row.serviceDescription}
                  onChange={(e) =>
                    updateCollectedRow(index, { serviceDescription: e.target.value })
                  }
                  disabled={!editable}
                  placeholder="e.g. monthly bookkeeping services"
                  data-testid={`collected-description-${index}`}
                />
              </div>
              {editable && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => removeCollectedRow(index)}
                  data-testid={`collected-remove-${index}`}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
          {editable && (
            <div className="flex items-center gap-space-3 flex-wrap">
              <Button
                variant="secondary"
                size="sm"
                onClick={addCollectedRow}
                disabled={form.nothingCollected}
                data-testid="collected-add-line"
              >
                Add a line
              </Button>
              <label className="flex items-center gap-space-2 text-small">
                <input
                  type="checkbox"
                  checked={form.nothingCollected}
                  onChange={(e) => update({ nothingCollected: e.target.checked })}
                  disabled={form.collected.length > 0}
                  className="accent-gold"
                  data-testid="nothing-collected-checkbox"
                />
                No fees this month
              </label>
            </div>
          )}
          {!editable && form.collected.length === 0 && (
            <p className="text-small text-gray-mid m-0">
              {form.nothingCollected
                ? "No fees this month."
                : "No fees reported."}
            </p>
          )}
          {quantityLabel && (
            <QuantityField
              label={quantityLabel}
              value={form.reportedQuantity}
              editable={editable}
              onChange={(value) => update({ reportedQuantity: value })}
            />
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-onyx/30 bg-white-warm p-space-4 flex flex-col gap-space-2">
          <h3 className="font-display font-semibold text-h4 m-0">4. This month's invoice</h3>
          <p className="text-caption font-medium text-onyx m-0">
            Private to Phoenix — not shared with the borrower.
          </p>
          <div className="flex flex-col gap-space-2 max-w-md">
            <Label htmlFor="invoice-amount">Invoice amount (USD)</Label>
            <Input
              id="invoice-amount"
              inputMode="decimal"
              value={form.invoiceAmount}
              onChange={(e) => update({ invoiceAmount: e.target.value })}
              disabled={!editable}
              placeholder="0.00"
              className="text-right tabular-nums"
              data-testid="invoice-amount-input"
            />
            <Label htmlFor="amount-explanation">How you calculated it</Label>
            <Input
              id="amount-explanation"
              value={form.amountCalculationExplanation}
              onChange={(e) => update({ amountCalculationExplanation: e.target.value })}
              disabled={!editable}
              placeholder="e.g. we received $4,200 in client fees; we owe 10% per our contract"
              data-testid="amount-explanation-input"
            />
            {quantityLabel && (
              <QuantityField
                label={quantityLabel}
                value={form.reportedQuantity}
                editable={editable}
                onChange={(value) => update({ reportedQuantity: value })}
              />
            )}
          </div>
        </div>
      )}

      {editable && (
        <div className="flex items-center gap-space-3 flex-wrap">
          <Button
            variant="primary"
            disabled={submitBlocked}
            onClick={() => setConfirming(true)}
            data-testid="submit-report"
          >
            Submit report
          </Button>
          {submitBlockedReason && (
            <span className="text-caption text-gray-mid" data-testid="submit-blocked-reason">
              {submitBlockedReason}
            </span>
          )}
          <AutosaveIndicator
            state={saveState}
            lastSavedAt={lastSavedAt}
            data-testid="report-autosave"
          />
        </div>
      )}

      {confirming && (
        <ConfirmSubmitModal
          form={form}
          isRegulated={isRegulated}
          submitting={submitting}
          submitError={submitError}
          onConfirm={() => submit(form)}
          onCancel={() => {
            setConfirming(false);
            setSubmitError(null);
          }}
        />
      )}
      {complaining && (
        <ComplaintModal
          assignmentId={report.assignmentId}
          reportId={report.id}
          onClose={() => setComplaining(false)}
        />
      )}
    </section>
  );
}

/** SP-4 WS-2 — the optional quantity behind the §4 amount, shown only when
 *  the vendor's contract terms are hourly (hours) or percent-of-value (the
 *  value). Lets Phoenix check the invoice math; leaving it blank never
 *  blocks a submit. */
function QuantityField({
  label,
  value,
  editable,
  onChange,
}: {
  label: string;
  value: string;
  editable: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex flex-col gap-space-1 max-w-md" data-testid="reported-quantity-field">
      <Label htmlFor="reported-quantity">{label}</Label>
      <Input
        id="reported-quantity"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        placeholder="0"
        className="text-right tabular-nums"
        data-testid="reported-quantity-input"
      />
      <p className="text-caption text-gray-mid m-0">
        Optional — helps Phoenix verify the amount against your contract terms.
      </p>
    </div>
  );
}

function PortalModal({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-onyx/50 p-space-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className="max-w-lg w-full rounded-md bg-white-pure p-space-5 shadow-lg border-t-2 border-t-gold flex flex-col gap-space-3"
      >
        {children}
      </div>
    </div>
  );
}

function ConfirmSubmitModal({
  form,
  isRegulated,
  submitting,
  submitError,
  onConfirm,
  onCancel,
}: {
  form: ReportFormState;
  isRegulated: boolean;
  submitting: boolean;
  submitError: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const completeCount = form.grades.filter((g) => g.isComplete).length;
  const amount = parseAmount(form.invoiceAmount);
  const validCollected = form.collected.filter(isValidCollectedRow);
  const collectedTotal = validCollected.reduce(
    (sum, row) => sum + (parseAmount(row.amount) ?? 0),
    0,
  );
  return (
    <PortalModal label="Submit this report?">
      <h2 className="font-display font-bold text-lead sm:text-h3 m-0">Submit this report?</h2>
      <ul className="m-0 p-0 list-none flex flex-col gap-space-1 text-small" data-testid="submit-summary">
        <li>
          <span className="font-medium">What we accomplished:</span>{" "}
          {form.publicNarrative.trim() ? "filled in" : "missing"}
        </li>
        <li>
          <span className="font-medium">Goal progress:</span> {form.grades.length} goal
          {form.grades.length === 1 ? "" : "s"} graded, {completeCount} marked complete
        </li>
        <li>
          <span className="font-medium">Concerns (private):</span>{" "}
          {form.privateConcerns.trim() ? "shared with Phoenix" : "none"}
        </li>
        {isRegulated ? (
          <li>
            <span className="font-medium">Fees for services (private):</span>{" "}
            {form.nothingCollected
              ? "no fees this month"
              : `${validCollected.length} line${validCollected.length === 1 ? "" : "s"}, ${collectedTotal.toLocaleString("en-US", { style: "currency", currency: "USD" })} — billed to the borrower through Phoenix`}
          </li>
        ) : (
          <li>
            <span className="font-medium">Invoice amount (private):</span>{" "}
            {amount != null
              ? amount.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })
              : "missing"}
          </li>
        )}
      </ul>
      <p className="text-caption text-gray-mid m-0">
        Phoenix Capital reviews every report. Only sections 1 and 2 are ever shown to the
        borrower.
      </p>
      {submitError && (
        <p className="text-small text-status-error m-0" role="alert" data-testid="submit-error">
          {submitError}
        </p>
      )}
      <div className="flex justify-end gap-space-2">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Keep editing
        </Button>
        <SubmitButton
          variant="primary"
          onClick={onConfirm}
          pending={submitting}
          pendingLabel="Submitting…"
          data-testid="confirm-submit"
        >
          Submit to Phoenix
        </SubmitButton>
      </div>
      <SubmittingHint pending={submitting} />
    </PortalModal>
  );
}

function ComplaintModal({
  assignmentId,
  reportId,
  onClose,
}: {
  assignmentId: string;
  reportId: string;
  onClose: () => void;
}) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);

  const complaint = useSubmittingMutation(
    async () => {
      await portalVendorApi.createComplaint({ assignmentId, reportId, text });
      toast.success("Your complaint was sent to Phoenix immediately.");
      onClose();
    },
    {
      onError: (err) =>
        setError(err instanceof Error ? err.message : "Couldn't send the complaint."),
    },
  );
  const busy = complaint.pending;

  const send = () => {
    setError(null);
    void complaint.submit();
  };

  return (
    <PortalModal label="Make a formal complaint">
      <h2 className="font-display font-bold text-lead sm:text-h3 m-0">Make a formal complaint</h2>
      <p className="text-small text-gray-mid m-0">
        This goes to Phoenix Capital immediately — it does not wait for your report, and the
        borrower never sees it.
      </p>
      <Textarea
        rows={4}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Describe the issue."
        aria-label="Complaint"
        data-testid="complaint-input"
      />
      {error && (
        <p className="text-small text-status-error m-0" role="alert">
          {error}
        </p>
      )}
      <div className="flex justify-end gap-space-2">
        <Button variant="secondary" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <SubmitButton
          variant="primary"
          onClick={send}
          pending={busy}
          pendingLabel="Sending…"
          disabled={text.trim() === ""}
          data-testid="send-complaint"
        >
          Send to Phoenix
        </SubmitButton>
      </div>
      <SubmittingHint pending={busy} />
    </PortalModal>
  );
}
